/**
 * ArchivedItemsPanel — slide-out panel showing archived cards and swimlanes
 * with restore functionality.
 *
 * Requirements: 16.1-16.6
 */

import React, { useCallback, useEffect, useState } from 'react';
import * as api from '../services/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ArchivedCard {
  readonly cardId: string;
  readonly title: string;
  readonly listId: string;
  readonly swimlaneId: string;
  readonly archivedAt: string | null;
}

export interface ArchivedSwimlane {
  readonly swimlaneId: string;
  readonly title: string;
  readonly color: string;
}

export interface ArchivedItemsPanelProps {
  readonly boardId: string;
  readonly archivedSwimlanes?: readonly ArchivedSwimlane[];
  readonly onClose: () => void;
  readonly onRestore: (cardId: string, listId: string, swimlaneId: string, title: string) => Promise<void>;
  readonly onRestoreSwimlane?: (swimlaneId: string) => Promise<void>;
}

interface ArchivedCardsResponse {
  readonly data: ArchivedCard[];
}

type TabId = 'cards' | 'swimlanes';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ArchivedItemsPanel({
  boardId,
  archivedSwimlanes = [],
  onClose,
  onRestore,
  onRestoreSwimlane,
}: ArchivedItemsPanelProps): React.ReactElement {
  const [activeTab, setActiveTab] = useState<TabId>('cards');
  const [archivedCards, setArchivedCards] = useState<ArchivedCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restoringIds, setRestoringIds] = useState<Set<string>>(new Set());
  const [restoringSwimlaneIds, setRestoringSwimlaneIds] = useState<Set<string>>(new Set());

  // Fetch archived cards on mount
  useEffect(() => {
    let cancelled = false;

    async function fetchArchived(): Promise<void> {
      setIsLoading(true);
      setError(null);
      try {
        const response = await api.get<ArchivedCardsResponse>(`/v1/boards/${boardId}/cards?archived=true`);
        if (!cancelled) {
          setArchivedCards(response.data);
          setIsLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError('Failed to load archived cards');
          setIsLoading(false);
        }
      }
    }

    fetchArchived();
    return () => { cancelled = true; };
  }, [boardId]);

  const handleRestoreCard = useCallback(async (card: ArchivedCard) => {
    setRestoringIds((prev) => new Set(prev).add(card.cardId));
    try {
      await onRestore(card.cardId, card.listId, card.swimlaneId, card.title);
      setArchivedCards((prev) => prev.filter((c) => c.cardId !== card.cardId));
    } catch {
      // Restore failed — keep in list
    } finally {
      setRestoringIds((prev) => {
        const next = new Set(prev);
        next.delete(card.cardId);
        return next;
      });
    }
  }, [onRestore]);

  const handleRestoreSwimlane = useCallback(async (swimlaneId: string) => {
    if (!onRestoreSwimlane) return;
    setRestoringSwimlaneIds((prev) => new Set(prev).add(swimlaneId));
    try {
      await onRestoreSwimlane(swimlaneId);
    } catch {
      // Restore failed — keep in list
    } finally {
      setRestoringSwimlaneIds((prev) => {
        const next = new Set(prev);
        next.delete(swimlaneId);
        return next;
      });
    }
  }, [onRestoreSwimlane]);

  return (
    <div
      className="fixed inset-y-0 right-0 w-80 bg-card border-l border-border shadow-xl z-40 overflow-y-auto"
      role="complementary"
      aria-label="Archived items"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h2 className="text-sm font-semibold text-card-foreground">Archived Items</h2>
        <button
          onClick={onClose}
          aria-label="Close archived items"
          className="p-1 rounded hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
        >
          ✕
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border" role="tablist" aria-label="Archived item type">
        <button
          role="tab"
          aria-selected={activeTab === 'cards'}
          aria-controls="panel-cards"
          id="tab-cards"
          onClick={() => setActiveTab('cards')}
          className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
            activeTab === 'cards'
              ? 'text-primary border-b-2 border-primary'
              : 'text-muted-foreground hover:text-card-foreground'
          }`}
        >
          Cards
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'swimlanes'}
          aria-controls="panel-swimlanes"
          id="tab-swimlanes"
          onClick={() => setActiveTab('swimlanes')}
          className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
            activeTab === 'swimlanes'
              ? 'text-primary border-b-2 border-primary'
              : 'text-muted-foreground hover:text-card-foreground'
          }`}
        >
          Swimlanes{archivedSwimlanes.length > 0 && ` (${archivedSwimlanes.length})`}
        </button>
      </div>

      {/* Cards Tab */}
      <div
        id="panel-cards"
        role="tabpanel"
        aria-labelledby="tab-cards"
        hidden={activeTab !== 'cards'}
        className="p-4"
      >
        {isLoading && (
          <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
            Loading archived cards...
          </p>
        )}

        {error && (
          <p className="text-sm text-destructive" role="alert">{error}</p>
        )}

        {!isLoading && !error && archivedCards.length === 0 && (
          <p className="text-sm text-muted-foreground italic">No archived cards</p>
        )}

        {!isLoading && !error && archivedCards.length > 0 && (
          <div className="space-y-2">
            {archivedCards.map((card) => (
              <div
                key={card.cardId}
                className="flex items-center justify-between p-2 border border-border rounded-md bg-muted/30"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-card-foreground truncate">{card.title}</p>
                  {card.archivedAt && (
                    <p className="text-xs text-muted-foreground">
                      Archived {new Date(card.archivedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleRestoreCard(card)}
                  disabled={restoringIds.has(card.cardId)}
                  className="ml-2 text-xs bg-primary text-primary-foreground px-2 py-1 rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                  aria-label={`Restore card: ${card.title}`}
                >
                  {restoringIds.has(card.cardId) ? 'Restoring...' : 'Restore'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Swimlanes Tab */}
      <div
        id="panel-swimlanes"
        role="tabpanel"
        aria-labelledby="tab-swimlanes"
        hidden={activeTab !== 'swimlanes'}
        className="p-4"
      >
        {archivedSwimlanes.length === 0 && (
          <p className="text-sm text-muted-foreground italic">No archived swimlanes</p>
        )}

        {archivedSwimlanes.length > 0 && (
          <div className="space-y-2">
            {archivedSwimlanes.map((swimlane) => (
              <div
                key={swimlane.swimlaneId}
                className="flex items-center justify-between p-2 border border-border rounded-md bg-muted/30"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {swimlane.color && (
                    <span
                      className="w-3 h-3 rounded-sm shrink-0"
                      style={{ backgroundColor: swimlane.color }}
                      aria-hidden="true"
                    />
                  )}
                  <p className="text-sm text-card-foreground truncate">{swimlane.title}</p>
                </div>
                <button
                  onClick={() => handleRestoreSwimlane(swimlane.swimlaneId)}
                  disabled={restoringSwimlaneIds.has(swimlane.swimlaneId)}
                  className="ml-2 text-xs bg-primary text-primary-foreground px-2 py-1 rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                  aria-label={`Restore swimlane: ${swimlane.title}`}
                >
                  {restoringSwimlaneIds.has(swimlane.swimlaneId) ? 'Restoring...' : 'Restore'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
